/**
 * React Query hooks for the Notification Center.
 *
 * - Reads: cached lists + RPC unread count, with periodic background refetch.
 * - Writes: thin wrappers around the notification service. After every
 *   mutation we invalidate the relevant query keys so the bell + page stay
 *   in sync.
 *
 * NOTE: No business logic lives here. Hooks are pure data plumbing — the
 * service layer is the single source of mutation behavior.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/hooks/useAuth";
import {
  archiveNotification,
  dismissNotification,
  dismissAllNotifications,
  getNotifications,
  getNotificationPreferences,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
  updateNotificationPreferences,
  adminCreateNotification,
  adminScheduleNotification,
  adminListNotificationTemplates,
  adminUpsertNotificationTemplate,
  adminArchiveNotificationTemplate,
  adminRunEmailTest,
  adminRetryDelivery,
  adminCancelDelivery,
  type AdminCreateNotificationPayload,
  type AdminCreateNotificationResult,
  type AdminScheduleNotificationPayload,
  type AdminScheduleNotificationResult,
  type AdminUpsertNotificationTemplatePayload,
  type NotificationTemplate,
  type AdminRunEmailTestResult,
  type AdminRetryDeliveryResult,
  type AdminCancelDeliveryResult,
  type GetNotificationsParams,
  type NotificationInboxItem,
  type NotificationPreference,
} from "@/services/notifications";

export const notificationKeys = {
  all: ["notifications"] as const,
  list: (params?: GetNotificationsParams) =>
    ["notifications", "list", params ?? {}] as const,
  unreadCount: ["notifications", "unread-count"] as const,
  preferences: ["notifications", "preferences"] as const,
  templates: (includeInactive?: boolean) =>
    ["notifications", "templates", { includeInactive: !!includeInactive }] as const,
  templatesAll: ["notifications", "templates"] as const,
};

// Polling kept as a safety net behind realtime. With `useNotificationRealtime`
// driving cache invalidation on every CDC event, we relax these intervals.
// The central realtime wrapper additionally provides a 30s polling fallback
// after exhausted reconnect attempts (offline / mobile sleep recovery).
const POLL_MS = 180_000;

/** Read notifications. Defaults: 50 items, hides dismissed/archived. */
export function useNotifications(params?: GetNotificationsParams) {
  const { user } = useAuth();
  return useQuery<NotificationInboxItem[]>({
    queryKey: notificationKeys.list(params),
    queryFn: () => getNotifications(params),
    enabled: !!user,
    staleTime: 60_000,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/** Unread in-app count via RPC. Realtime drives sub-second updates; polling
 * is kept as a long-interval safety net. */
export function useUnreadNotificationCount() {
  const { user } = useAuth();
  return useQuery<number>({
    queryKey: notificationKeys.unreadCount,
    queryFn: getUnreadNotificationCount,
    enabled: !!user,
    staleTime: 60_000,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}

function useInvalidateAll() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: notificationKeys.all });
  };
}

/**
 * Apply a patch to every cached notifications list. We walk all queries
 * starting with `notifications/list` because the list is keyed by the
 * filter params and there may be several variants in cache (bell vs page).
 */
function patchListCaches(
  qc: ReturnType<typeof useQueryClient>,
  updater: (item: NotificationInboxItem) => NotificationInboxItem,
): Array<{ key: readonly unknown[]; data: NotificationInboxItem[] | undefined }> {
  const entries = qc.getQueriesData<NotificationInboxItem[]>({
    queryKey: ["notifications", "list"],
  });
  const snapshot: Array<{
    key: readonly unknown[];
    data: NotificationInboxItem[] | undefined;
  }> = [];
  for (const [key, data] of entries) {
    snapshot.push({ key, data });
    if (!Array.isArray(data)) continue;
    qc.setQueryData<NotificationInboxItem[]>(key, data.map(updater));
  }
  return snapshot;
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, string, {
    listSnapshot: Array<{ key: readonly unknown[]; data: NotificationInboxItem[] | undefined }>;
    prevUnread: number | undefined;
  }>({
    mutationFn: (inboxId: string) => markNotificationRead(inboxId),
    // Lazy/optimistic status flip: UI updates immediately, the network
    // round-trip (and cache invalidation) happens in the background.
    onMutate: async (inboxId) => {
      await qc.cancelQueries({ queryKey: notificationKeys.all });
      const nowIso = new Date().toISOString();
      const listSnapshot = patchListCaches(qc, (item) =>
        item.inbox_id === inboxId && item.status === "unread"
          ? { ...item, status: "read", read_at: item.read_at ?? nowIso }
          : item,
      );
      const prevUnread = qc.getQueryData<number>(notificationKeys.unreadCount);
      if (typeof prevUnread === "number" && prevUnread > 0) {
        qc.setQueryData<number>(notificationKeys.unreadCount, prevUnread - 1);
      }
      return { listSnapshot, prevUnread };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.listSnapshot) {
        qc.setQueryData(key, data);
      }
      if (typeof ctx.prevUnread === "number") {
        qc.setQueryData(notificationKeys.unreadCount, ctx.prevUnread);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation<{ updated_count: number }, Error, string | undefined, {
    listSnapshot: Array<{ key: readonly unknown[]; data: NotificationInboxItem[] | undefined }>;
    prevUnread: number | undefined;
  }>({
    mutationFn: (type?: string) => markAllNotificationsRead(type),
    // Lazy/optimistic mass flip: the badge + every visible row switch to
    // "read" before the RPC returns, so the user never stares at unchanged
    // UI while the backend processes the batch.
    onMutate: async (type) => {
      await qc.cancelQueries({ queryKey: notificationKeys.all });
      const nowIso = new Date().toISOString();
      const matches = (item: NotificationInboxItem) =>
        item.status === "unread" && (type ? item.type === type : true);
      const listSnapshot = patchListCaches(qc, (item) =>
        matches(item)
          ? { ...item, status: "read", read_at: item.read_at ?? nowIso }
          : item,
      );
      const prevUnread = qc.getQueryData<number>(notificationKeys.unreadCount);
      if (typeof prevUnread === "number") {
        if (type) {
          // Conservative decrement when filtering by type: count how many
          // matching unread rows we just flipped across all cached lists.
          const flipped = new Set<string>();
          for (const { data } of listSnapshot) {
            if (!Array.isArray(data)) continue;
            for (const it of data) if (matches(it)) flipped.add(it.inbox_id);
          }
          qc.setQueryData<number>(
            notificationKeys.unreadCount,
            Math.max(0, prevUnread - flipped.size),
          );
        } else {
          qc.setQueryData<number>(notificationKeys.unreadCount, 0);
        }
      }
      return { listSnapshot, prevUnread };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.listSnapshot) {
        qc.setQueryData(key, data);
      }
      if (typeof ctx.prevUnread === "number") {
        qc.setQueryData(notificationKeys.unreadCount, ctx.prevUnread);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation<void, Error, string, {
    listSnapshot: Array<{ key: readonly unknown[]; data: NotificationInboxItem[] | undefined }>;
    prevUnread: number | undefined;
  }>({
    mutationFn: (inboxId: string) => dismissNotification(inboxId),
    // Optimistic remove: the row disappears from every cached list
    // immediately and the unread count decrements when the item was unread.
    // The deleted-filter list is repopulated via the onSettled invalidation.
    onMutate: async (inboxId) => {
      await qc.cancelQueries({ queryKey: notificationKeys.all });
      const entries = qc.getQueriesData<NotificationInboxItem[]>({
        queryKey: ["notifications", "list"],
      });
      const listSnapshot: Array<{
        key: readonly unknown[];
        data: NotificationInboxItem[] | undefined;
      }> = [];
      let wasUnread = false;
      for (const [key, data] of entries) {
        listSnapshot.push({ key, data });
        if (!Array.isArray(data)) continue;
        const target = data.find((it) => it.inbox_id === inboxId);
        if (target && target.status === "unread") wasUnread = true;
        qc.setQueryData<NotificationInboxItem[]>(
          key,
          data.filter((it) => it.inbox_id !== inboxId),
        );
      }
      const prevUnread = qc.getQueryData<number>(notificationKeys.unreadCount);
      if (wasUnread && typeof prevUnread === "number" && prevUnread > 0) {
        qc.setQueryData<number>(notificationKeys.unreadCount, prevUnread - 1);
      }
      return { listSnapshot, prevUnread };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.listSnapshot) {
        qc.setQueryData(key, data);
      }
      if (typeof ctx.prevUnread === "number") {
        qc.setQueryData(notificationKeys.unreadCount, ctx.prevUnread);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useArchiveNotification() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (inboxId: string) => archiveNotification(inboxId),
    onSuccess: invalidate,
  });
}

/**
 * Bulk delete (= dismiss) every notification for the current user. Patches
 * all cached lists to empty arrays immediately so the bell panel and the
 * notifications page clear in lockstep, even when the user has many rows.
 */
export function useDismissAllNotifications() {
  const qc = useQueryClient();
  return useMutation<{ updated_count: number }, Error, string | undefined, {
    listSnapshot: Array<{ key: readonly unknown[]; data: NotificationInboxItem[] | undefined }>;
    prevUnread: number | undefined;
  }>({
    mutationFn: (type?: string) => dismissAllNotifications(type),
    onMutate: async (type) => {
      await qc.cancelQueries({ queryKey: notificationKeys.all });
      const entries = qc.getQueriesData<NotificationInboxItem[]>({
        queryKey: ["notifications", "list"],
      });
      const listSnapshot: Array<{
        key: readonly unknown[];
        data: NotificationInboxItem[] | undefined;
      }> = [];
      for (const [key, data] of entries) {
        listSnapshot.push({ key, data });
        if (!Array.isArray(data)) continue;
        qc.setQueryData<NotificationInboxItem[]>(
          key,
          type ? data.filter((it) => it.type !== type) : [],
        );
      }
      const prevUnread = qc.getQueryData<number>(notificationKeys.unreadCount);
      if (!type) {
        qc.setQueryData<number>(notificationKeys.unreadCount, 0);
      }
      return { listSnapshot, prevUnread };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.listSnapshot) {
        qc.setQueryData(key, data);
      }
      if (typeof ctx.prevUnread === "number") {
        qc.setQueryData(notificationKeys.unreadCount, ctx.prevUnread);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateNotificationPreferences,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.preferences });
      // In-app preference changes can later affect inbox visibility.
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/** Read all notification preferences for the current user. */
export function useNotificationPreferences() {
  const { user } = useAuth();
  return useQuery<NotificationPreference[]>({
    queryKey: notificationKeys.preferences,
    queryFn: getNotificationPreferences,
    enabled: !!user,
    staleTime: 60_000,
  });
}

/**
 * Admin composer mutation. Invalidates inbox queries on success so the
 * sender's own bell reflects the message immediately when audience='self'
 * or 'admins' includes them.
 */
export function useAdminCreateNotification() {
  const invalidate = useInvalidateAll();
  return useMutation<
    AdminCreateNotificationResult,
    Error,
    AdminCreateNotificationPayload
  >({
    mutationFn: adminCreateNotification,
    onSuccess: invalidate,
  });
}

/**
 * Admin scheduling mutation. Does NOT invalidate inbox queries — the
 * notification has not been created yet; the worker will create it at
 * `scheduled_for`. Realtime + polling will pick it up then.
 */
export function useAdminScheduleNotification() {
  return useMutation<
    AdminScheduleNotificationResult,
    Error,
    AdminScheduleNotificationPayload
  >({
    mutationFn: adminScheduleNotification,
  });
}

/* ── Notification Templates ─────────────────────────────────────────── */

export function useAdminNotificationTemplates(includeInactive = false) {
  const { user } = useAuth();
  return useQuery<NotificationTemplate[]>({
    queryKey: notificationKeys.templates(includeInactive),
    queryFn: () => adminListNotificationTemplates(includeInactive),
    enabled: !!user,
    staleTime: 60_000,
  });
}

export function useAdminUpsertNotificationTemplate() {
  const qc = useQueryClient();
  return useMutation<
    NotificationTemplate,
    Error,
    AdminUpsertNotificationTemplatePayload
  >({
    mutationFn: adminUpsertNotificationTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.templatesAll });
    },
  });
}

export function useAdminArchiveNotificationTemplate() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: adminArchiveNotificationTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.templatesAll });
    },
  });
}

/**
 * Admin-only: triggers an end-to-end email pipeline self-test. The mutation
 * resolves immediately after fan-out — the UI then polls the resulting
 * delivery row separately to surface the terminal status.
 */
export function useAdminRunEmailTest() {
  const qc = useQueryClient();
  return useMutation<
    AdminRunEmailTestResult,
    Error,
    { title?: string; body?: string } | void
  >({
    mutationFn: (input) => adminRunEmailTest(input || {}),
    onSuccess: () => {
      // Refresh inbox + delivery list so the just-created records show up.
      qc.invalidateQueries({ queryKey: notificationKeys.all });
      qc.invalidateQueries({ queryKey: ["admin-notification-deliveries"] });
    },
  });
}

/**
 * Admin-only: re-enqueue a failed/skipped notification delivery so the
 * worker tries to send it again. Invalidates the delivery list so the row
 * flips back to `queued` immediately.
 */
export function useAdminRetryDelivery() {
  const qc = useQueryClient();
  return useMutation<AdminRetryDeliveryResult, Error, string>({
    mutationFn: (deliveryId) => adminRetryDelivery(deliveryId),
    onSuccess: async (result) => {
      // First refetch — picks up the immediate `queued` state and any
      // sync-triggered worker outcome (sent/failed) that has already
      // landed by the time the response returned.
      await qc.refetchQueries({ queryKey: ["admin-notification-deliveries"] });

      // If the backend confirms the worker was triggered, schedule a
      // second refetch shortly after so we surface the post-tick terminal
      // state (sent / failed) without the operator having to poll.
      if (result.worker_triggered) {
        window.setTimeout(() => {
          qc.refetchQueries({ queryKey: ["admin-notification-deliveries"] });
        }, 800);
      }
    },
  });
}

/**
 * Admin-only: cancel a non-`sent` delivery so the worker stops trying to
 * send it. The row flips to `cancelled` and any in-flight job is cancelled
 * server-side. Sent deliveries cannot be cancelled.
 */
export function useAdminCancelDelivery() {
  const qc = useQueryClient();
  return useMutation<
    AdminCancelDeliveryResult,
    Error,
    { deliveryId: string; reason?: string }
  >({
    mutationFn: ({ deliveryId, reason }) => adminCancelDelivery(deliveryId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-notification-deliveries"] });
    },
  });
}